import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  Typography, Select, DatePicker, Table, Tag, message, Space,
} from 'antd';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { formatTS } from '../utils/time';
import api from '../api';

const { RangePicker } = DatePicker;

const STATUS_OPTIONS = [
  { label: 'Completed',         value: 'completed' },
  { label: 'Rescore Requested', value: 'rescore_requested' },
];

const STATUS_LABEL = {
  assigned:          'Draft',
  in_progress:       'In Progress',
  completed:         'Completed',
  rescore_requested: 'Rescore Requested',
  canceled:          'Canceled',
};

const STATUS_COLOR = {
  assigned:          'blue',
  in_progress:       'orange',
  completed:         'green',
  rescore_requested: 'purple',
  canceled:          'default',
};

const TYPE_OPTIONS = [
  { label: 'Evaluations',   value: 'evaluation' },
  { label: 'Feedback Only', value: 'feedback_only' },
  { label: 'All Types',     value: 'all' },
];

let _cache = null;

export default function MyEvalsPage() {
  const navigate = useNavigate();

  const defaultCallRange = [dayjs().subtract(3, 'month').startOf('month'), dayjs()];

  const restoredRef = useRef(!!_cache);
  const [cases,         setCases]         = useState(() => _cache?.cases         ?? []);
  const [loading,       setLoading]       = useState(false);
  const [callDateRange, setCallDateRange] = useState(() => _cache?.callDateRange ?? defaultCallRange);
  const [evalDateRange, setEvalDateRange] = useState(() => _cache?.evalDateRange ?? null);
  const [status,        setStatus]        = useState(() => _cache?.status        ?? []);
  const [evalType,      setEvalType]      = useState(() => _cache?.evalType      ?? 'all');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { my_evals: '1' };

      if (callDateRange?.[0]) params.start_date = callDateRange[0].format('YYYY-MM-DD');
      if (callDateRange?.[1]) params.end_date   = callDateRange[1].format('YYYY-MM-DD');
      if (evalDateRange?.[0]) params.eval_created_start = evalDateRange[0].format('YYYY-MM-DD');
      if (evalDateRange?.[1]) params.eval_created_end   = evalDateRange[1].format('YYYY-MM-DD');
      if (status.length)      params.status = status.join(',');

      if (evalType === 'feedback_only') params.is_feedback_only     = '1';
      if (evalType === 'all')           params.include_feedback_only = '1';

      const res = await api.get('/api/cases', { params });
      setCases(res.data);
      _cache = { cases: res.data, callDateRange, evalDateRange, status, evalType };
    } catch {
      message.error('Failed to load evaluations.');
    } finally {
      setLoading(false);
    }
  }, [callDateRange, evalDateRange, status, evalType]);

  useEffect(() => {
    if (restoredRef.current) { restoredRef.current = false; return; }
    load();
  }, [load]);

  const columns = [
    {
      title:     'Contact ID',
      dataIndex: 'ContactID',
      key:       'ContactID',
      width:     110,
      // An evaluation without a call shows its External Ref instead.
      render:    (v, r) => v ?? r.ExternalRef,
    },
    {
      title:     'Type',
      dataIndex: 'IsFeedbackOnly',
      key:       'Type',
      width:     130,
      render:    (v) => v
        ? <Tag color="purple">Feedback Only</Tag>
        : <Tag color="blue">Evaluation</Tag>,
    },
    {
      title:     'Status',
      dataIndex: 'Status',
      key:       'Status',
      width:     160,
      render:    (v) => <Tag color={STATUS_COLOR[v] ?? 'default'}>{STATUS_LABEL[v] ?? v}</Tag>,
    },
    {
      title:     'Agent',
      dataIndex: 'AgentName',
      key:       'AgentName',
      width:     150,
    },
    {
      title:     'Evaluator',
      dataIndex: 'AssignedToName',
      key:       'AssignedToName',
      width:     160,
    },
    {
      title:     'Form',
      dataIndex: 'FormName',
      key:       'FormName',
      ellipsis:  true,
      render:    (name, r) =>
        name ? `${name}${r.FormVersion ? ` v${r.FormVersion}` : ''}` : '—',
    },
    {
      title:     'Call Date',
      dataIndex: 'CallDate',
      key:       'CallDate',
      width:     110,
      sorter:    (a, b) => (a.CallDate ?? '').localeCompare(b.CallDate ?? ''),
      render:    (v) => v?.slice(0, 10) ?? '—',
    },
    {
      title:     'Campaign',
      dataIndex: 'Campaign',
      key:       'Campaign',
      width:     140,
      render:    (v) => v ?? '—',
    },
    {
      title:     'Score',
      dataIndex: 'TotalEarned',
      key:       'TotalEarned',
      width:     100,
      align:     'right',
      sorter:    (a, b) => (a.TotalEarned ?? 0) - (b.TotalEarned ?? 0),
      render:    (v, r) =>
        r.TotalPossible > 0
          ? `${v} / ${r.TotalPossible}`
          : <Typography.Text type="secondary">—</Typography.Text>,
    },
    {
      title:     'Completed On',
      dataIndex: 'CompletedAt',
      key:       'CompletedAt',
      width:     155,
      sorter:    (a, b) => (a.CompletedAt ?? '').localeCompare(b.CompletedAt ?? ''),
      defaultSortOrder: 'descend',
      render:    (v) => formatTS(v),
    },
    {
      title:  'Ack Due',
      key:    'AckDue',
      width:  105,
      render: (_, r) => {
        if (r.Status !== 'completed' || !r.CompletedAt) return <Typography.Text type="secondary">—</Typography.Text>;
        if (r.AcknowledgedAt) return <Typography.Text type="secondary">Acknowledged</Typography.Text>;
        const dl = dayjs(r.CompletedAt).add(r.DisputeWindowDays ?? 4, 'day');
        const past = dayjs().isAfter(dl);
        return <span style={{ color: past ? '#8c8c8c' : '#fa8c16', fontWeight: past ? undefined : 600 }}>{dl.format('MM/DD/YY')}</span>;
      },
    },
    {
      title:     'Eval Created',
      dataIndex: 'CreatedAt',
      key:       'CreatedAt',
      width:     155,
      sorter:    (a, b) => (a.CreatedAt ?? '').localeCompare(b.CreatedAt ?? ''),
      render:    (v) => formatTS(v),
    },
  ];

  return (
    <>
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        marginBottom: 16, flexWrap: 'wrap', gap: 8,
      }}>
        <Typography.Title level={4} style={{ margin: 0 }}>My Evaluations</Typography.Title>

        <Space wrap>
          <RangePicker
            placeholder={['Call Date From', 'Call Date To']}
            value={callDateRange}
            onChange={(v) => setCallDateRange(v ?? defaultCallRange)}
            format="YYYY-MM-DD"
          />
          <RangePicker
            placeholder={['Eval Created From', 'Eval Created To']}
            value={evalDateRange}
            onChange={(v) => setEvalDateRange(v ?? null)}
            format="YYYY-MM-DD"
          />
          <Select
            mode="multiple"
            allowClear
            placeholder="All statuses"
            options={STATUS_OPTIONS}
            value={status}
            onChange={setStatus}
            maxTagCount="responsive"
            style={{ minWidth: 170 }}
          />
          <Select
            options={TYPE_OPTIONS}
            value={evalType}
            onChange={setEvalType}
            style={{ width: 155 }}
          />
        </Space>
      </div>

      <Table
        rowKey="ID"
        dataSource={cases}
        columns={columns}
        loading={loading}
        size="small"
        scroll={{ x: 'max-content' }}
        pagination={{
          defaultPageSize: 50,
          showSizeChanger: true,
          pageSizeOptions: ['25', '50', '100'],
          showTotal: (t) => `${t} evaluation${t === 1 ? '' : 's'}`,
        }}
        onRow={(r) => ({
          onClick:  () => navigate(`/cases/${r.ID}`),
          style: { cursor: 'pointer' },
        })}
        locale={{ emptyText: 'No evaluations found for the selected filters.' }}
      />
    </>
  );
}
